"use client";

import React from "react";
import { Mail, Phone, MapPin, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const DirectConnect = () => {
  return <section id="direct-connect" className="dark bg-background text-foreground relative w-full overflow-hidden">
      <div className="flex flex-col lg:flex-row min-h-[800px]">
        {}
        <div className="w-full lg:w-[60%] relative min-h-[400px] lg:min-h-full bg-muted/10">
          <div className="absolute inset-0 w-full h-full">
            <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d255281.19036281522!2d36.7073073589624!3d-1.3031933719272915!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x182f1172d84d49a7%3A0xf7cf0254b297924c!2sNairobi%2C%20Kenya!5e0!3m2!1sen!2sus!4v1716300000000!5m2!1sen!2sus" width="100%" height="100%" style={{
            border: 0
          }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" className="w-full h-full grayscale invert brightness-[0.8] contrast-[1.2] opacity-80 hover:opacity-100 transition-opacity duration-500" title="Inara Technologies Location" />
            {}
            <div className="absolute inset-0 bg-gradient-to-r from-background/80 to-transparent pointer-events-none lg:via-transparent lg:to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent pointer-events-none lg:hidden" />
          </div>
          
          {}
          <div className="absolute bottom-8 left-8 z-10 bg-card/90 backdrop-blur-md border border-border p-6 rounded-xl shadow-2xl max-w-xs hidden md:block">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-primary/10 rounded-lg text-primary shrink-0">
                <MapPin className="size-6" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-foreground mb-1">HQ Location</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Nairobi, Kenya
                </p>
                <p className="text-xs text-primary mt-2 font-medium">
                  Mon-Fri: 9am-6pm
                </p>
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="w-full lg:w-[40%] bg-background border-l border-border/50 flex flex-col justify-center p-8 lg:p-12 xl:p-16 relative">
          <div className="max-w-lg mx-auto w-full space-y-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-3">
                Direct <span className="text-primary">Connect</span>
              </h2>
              <p className="text-muted-foreground">
                Reach out to our Nairobi team to discuss your next big digital move.
              </p>
            </div>

            <FormProvider formId="direct_connect_form_connect" sectionName="direct-connect" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            company: "",
            inquiry_type: "",
            message: ""
          }} validationRules={{
            full_name: {
              required: "Full Name is required"
            },
            email: {
              required: "Email is required",
              pattern: "email"
            },
            phone: {
              required: "Phone Number is required"
            },
            inquiry_type: {
              required: "Please select an inquiry type"
            },
            message: {
              required: "Message is required"
            }
          }} submitText="Send Message" successMessage="Thank you! We've received your inquiry and will be in touch shortly.">
              <ConnectForm />
            </FormProvider>

            {}
            <div className="pt-8 border-t border-border grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="flex items-center gap-3 group">
                <div className="size-10 rounded-full bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <Mail className="size-5" />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Email Us</span>
                  <Link to="mailto:contact@inara.tech" className="text-sm font-semibold hover:text-primary transition-colors">
                    contact@inara.tech
                  </Link>
                </div>
              </div>

              <div className="flex items-center gap-3 group">
                <div className="size-10 rounded-full bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <Phone className="size-5" />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Call Us</span>
                  <Link to="tel:+254712345678" className="text-sm font-semibold hover:text-primary transition-colors">
                    +254 712 345 678
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
function ConnectForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-6 bg-primary/5 border border-primary/20 rounded-lg animate-in fade-in zoom-in duration-300">
        <div className="flex flex-col items-center text-center space-y-3">
          <div className="size-12 rounded-full bg-primary/20 flex items-center justify-center text-primary mb-2">
            <Mail className="size-6" />
          </div>
          <h3 className="text-xl font-semibold text-foreground">Message Sent!</h3>
          <p className="text-muted-foreground">{successMessage}</p>
          <Button variant="outline" className="mt-4 border-primary/30 hover:bg-primary/10 hover:text-primary" onClick={() => window.location.reload()}>
            Send Another Message
          </Button>
        </div>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="full_name" render={({
        field
      }) => <FormItem>
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" {...field} className="bg-input/30 border-input/50 focus:border-primary/50 focus:ring-primary/20" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" {...field} className="bg-input/30 border-input/50 focus:border-primary/50 focus:ring-primary/20" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+254 7xx xxx xxx" {...field} className="bg-input/30 border-input/50 focus:border-primary/50 focus:ring-primary/20" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel>Company / Organization</FormLabel>
              <FormControl>
                <Input placeholder="Acme Corp" {...field} className="bg-input/30 border-input/50 focus:border-primary/50 focus:ring-primary/20" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="inquiry_type" render={({
      field
    }) => <FormItem>
            <FormLabel>Inquiry Type</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="bg-input/30 border-input/50 focus:border-primary/50 focus:ring-primary/20">
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="AI Consulting">AI Consulting</SelectItem>
                <SelectItem value="Cloud Migration">Cloud Migration</SelectItem>
                <SelectItem value="Automation Solutions">Automation Solutions</SelectItem>
                <SelectItem value="General Inquiry">General Inquiry</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel>Message</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us how we can help..." className="min-h-[120px] bg-input/30 border-input/50 focus:border-primary/50 focus:ring-primary/20 resize-none" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-sm text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-medium bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(6,182,212,0.3)] hover:shadow-[0_0_30px_rgba(6,182,212,0.5)] transition-all duration-300">
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Sending...
          </> : "Send Message"}
      </Button>
    </Form>;
}
export default DirectConnect;